import React from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { ColorStyle } from '../api'
import { ChannelRowData, getDotColor, getTextTintColor } from './channelRowHelpers'
import { RowColorMark, RowInfo, RowMeta, RowMetaLine, RowTitle } from './rowStyles'
import { DynamicChannelBadge } from './DynamicChannelBadge'

type Props = {
    row: ChannelRowData
    colorStyle: ColorStyle
    colorTextEnabled: boolean
    onReset: (mnemonic: string) => void
}

// no drag handle, no Draggable wrapper - this list is a short, occasional edge case (a dynamic
// channel a user explicitly removed), not a primary curation surface like Available/Selected
export const RemovedChannelRow = React.memo(function RemovedChannelRow({
    row,
    colorStyle,
    colorTextEnabled,
    onReset
}: Props): JSX.Element {
    const { metadata } = row
    const info = metadata.category ? [metadata.mnemonic, `Category: ${metadata.category}`] : [metadata.mnemonic]
    const tintColor = colorTextEnabled ? getTextTintColor(row) : null

    return (
        <Row>
            <RowColorMark color={getDotColor(row)} shape={colorStyle} />
            <RowInfo>
                <RowTitle style={tintColor ? { color: tintColor } : undefined}>{metadata.title}</RowTitle>
                <RowMetaLine>
                    <DynamicChannelBadge />
                    <RowMeta>{info.join(' · ')}</RowMeta>
                </RowMetaLine>
            </RowInfo>
            <ResetButton onClick={() => onReset(metadata.mnemonic)}>
                <i className="fa fa-undo" />
                {i18n('Reset')}
            </ResetButton>
        </Row>
    )
})

const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    height: 48px;
    padding: 0 16px 0 12px;

    &:not(:last-child) {
        border-bottom: 0.5px solid var(--gray-300);
    }
`

const ResetButton = styled.button`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 18px;
    color: var(--blue-500);
    background: none;
    border: none;
    padding: 4px 6px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
        background-color: var(--gray-200);
    }
`
